import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { naira, fmtDateTime, titleCase } from "../lib/format";

/** Full itemised order view — shared by Orders, Manager Console and the KDS so
 *  a completed order is never a dead end (QA #36). */
export default function OrderDetailDialog({ orderId, order: seed, open, onClose }) {
  const [order, setOrder] = useState(seed || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const id = orderId || seed?.id || seed?.order_id;
    if (!id) { setOrder(seed || null); return; }
    setLoading(true);
    api.get(`/orders/${id}`).then(setOrder).catch(() => setOrder(seed || null)).finally(() => setLoading(false));
  }, [open, orderId, seed]);

  const items = order?.items || order?.lines || [];
  const money = (v) => naira(Number(v || 0));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="order-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-brand" style={{ color: "#8B1E1E" }}>
            {order?.number || "Order"} {order?.fulfilment_status && <Badge className="ml-2 align-middle">{titleCase(order.fulfilment_status)}</Badge>}
          </DialogTitle>
        </DialogHeader>
        {loading && <div className="text-sm" style={{ color: "#a58f7c" }}>Loading order…</div>}
        {order && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <Meta k="Customer" v={order.customer_name || "Walk-in"} testid="od-customer" />
              <Meta k="Served by" v={order.served_by_name || order.cashier_name || "—"} testid="od-agent" />
              <Meta k="Order type" v={titleCase(order.order_type || "—")} />
              <Meta k="Channel" v={(order.channel || "—").toUpperCase()} />
              <Meta k="Till" v={order.till || "—"} />
              <Meta k="Table" v={order.table || "—"} />
              <Meta k="Branch" v={order.location_name || "—"} />
              <Meta k="Time" v={fmtDateTime(order.date || order.created_at)} />
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide mb-2" style={{ color: "#a58f7c" }}>Items ({items.length})</div>
              <table className="w-full" data-testid="od-items">
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} className="border-b" style={{ borderColor: "#F0E7DA" }}>
                      <td className="py-2 pr-2">
                        <div className="font-medium">{it.name}{it.size ? ` · ${it.size}` : ""}</div>
                        {(it.modifiers || []).length > 0 && (
                          <div className="text-xs" style={{ color: "#a58f7c" }}>+ {(it.modifiers || []).map((m) => m.name || m).join(", ")}</div>
                        )}
                        {it.note && <div className="text-xs" style={{ color: "#B7791F" }}>Note: {it.note}</div>}
                      </td>
                      <td className="py-2 text-center w-12">×{it.quantity}</td>
                      <td className="py-2 text-right w-24">{money((it.unit_price ?? it.price ?? 0) * (it.quantity || 1))}</td>
                    </tr>
                  ))}
                  {items.length === 0 && <tr><td className="py-3" style={{ color: "#a58f7c" }}>No line items recorded.</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="space-y-1">
              {[["Subtotal", order.subtotal], ["Discount", order.discount ? -order.discount : 0],
                ["Service charge", order.service_charge], ["VAT", order.tax]]
                .filter(([, v]) => Number(v || 0) !== 0)
                .map(([k, v]) => <Line key={k} k={k} v={money(v)} />)}
              <Line k="Total" v={money(order.total)} strong />
              {(order.payments || []).map((p, i) => <Line key={i} k={`Paid · ${titleCase(p.method)}`} v={money(p.amount)} />)}
              {Number(order.refunded_amount || 0) > 0 && <Line k="Refunded" v={money(order.refunded_amount)} />}
            </div>
            {order.notes && <div className="text-xs" style={{ color: "#765A4A" }}>Order note: {order.notes}</div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Meta = ({ k, v, testid }) => (
  <div>
    <div className="text-xs uppercase tracking-wide" style={{ color: "#a58f7c" }}>{k}</div>
    <div className="font-medium" data-testid={testid}>{v}</div>
  </div>
);

const Line = ({ k, v, strong }) => (
  <div className="flex justify-between" style={{ fontWeight: strong ? 700 : 400, color: strong ? "#8B1E1E" : "#3B2722" }}>
    <span>{k}</span><span>{v}</span>
  </div>
);
